import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import Layout from "components/yenimahalle/Layout";
import BasicHeader from "components/yenimahalle/BasicHeader";
import ContentText from "components/yenimahalle/ContentText";
import PortalPeopleApi from "../../api/PortalPeopleApi";
import { ActivityReport, ManagerList } from "../../models/Response/PortalPeople";
import tw from "twin.macro";
import styled from "styled-components/macro";
import { CustomButton, CustomModal } from "albinasoft-ui-package";

const PageContainer = tw.div`
  pt-8 pb-16 px-4
  flex flex-col lg:flex-row
  justify-between items-start
  max-w-screen-xl mx-auto
  space-y-8 lg:space-y-0 lg:space-x-8
`;

const LeftColumn = tw.div`w-full lg:w-9/12`;
const RightColumn = tw.div`w-full lg:w-3/12 mt-8 lg:mt-0`;
const Content = tw.div`max-w-screen-xl mx-auto py-8`;

const Heading = tw.span`
  inline-block my-4 pl-3 py-1 text-xl font-bold border-l-4 border-primary-500
`;

// Kart tasarımı
const Card = tw.div`
  h-full flex! flex-col 
  sm:border 
  sm:rounded-tl-4xl sm:rounded-br-5xl 
  relative focus:outline-none
  bg-primary-100
`;

interface CardImageProps {
  imageSrc: string;
}
const CardImage = styled.div<CardImageProps>((props) => [
  `background-image: url("${props.imageSrc}");`,
  tw`sm:h-118 bg-cover bg-center rounded sm:rounded-none sm:rounded-tl-4xl`,
]);

const TextInfo = tw.div`py-6 sm:px-10 sm:py-6`;
const TitleReviewContainer = tw.div`flex flex-col sm:flex-row sm:justify-between sm:items-center mt-5`;
const Title = tw.h5`text-2xl font-bold`;
const Description = tw.p`text-sm leading-loose mt-2 sm:mt-4 whitespace-pre-line`;

// Rapor linkleri için kullanılan stil
const ReportLink = styled.a`
  ${tw`
    block p-4 mb-4 border border-gray-300 rounded-lg 
    hover:bg-primary-100 hover:border-primary-500 
    transition-colors duration-200
  `}
  text-decoration: none;
  color: inherit;
  cursor: pointer;
`;

const Label = tw.span`
  text-lg 
  font-semibold
`;
// Yıl seçimi için styled select
const YearSelect = tw.select`
  border border-gray-300 rounded p-2 mb-4 text-sm
  focus:outline-none focus:border-primary-500
`;

const MudurlukDetay: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const [manager, setManager] = useState<ManagerList | null>(null);
  const [activities, setActivities] = useState<ActivityReport[] | []>([]);
  const [selectedYear, setSelectedYear] = useState("2025");
  const [showModal, setShowModal] = useState<boolean>(false);
  const [selectedActivity, setSelectedActivity] = useState<ActivityReport | null>(null);

  // Yıl seçenekleri (2025'ten 2020'ye kadar)
  const years = [2025, 2024, 2023, 2022, 2021, 2020];

  useEffect(() => {
    const fetchData = async () => {
      if (id) {
        const result = await PortalPeopleApi.getMudurlukDetay(Number(id));
        if (result) {
          setManager(result);
          setActivities(result.activityReports);
        }
      }
    };
    fetchData();
  }, [id, selectedYear]);

  if (!manager) {
    return <div>Yükleniyor...</div>;
  }

  return (
    <Layout>
      <BasicHeader headingLine1={manager.directorateName} headingLine2="" />
      <PageContainer>
        <LeftColumn>
          <Card>
            <CardImage imageSrc={manager.managerImageUrl} />
            <TextInfo>
              <TitleReviewContainer>
                <Title>Görev ve Sorumluluklar</Title>
              </TitleReviewContainer>
              <Description>{manager.gorevSorumluluk}</Description>
              <TitleReviewContainer>
                <Title>Misyon</Title>
              </TitleReviewContainer>
              <Description>{manager.misyon}</Description>
              <TitleReviewContainer>
                <Title>Vizyon</Title>
              </TitleReviewContainer>
              <Description>{manager.vizyon}</Description>
              <TitleReviewContainer>
                <Title>İstek ve Şikayetleriniz İçin</Title>
              </TitleReviewContainer>
              <Description>
                <a href="https://iletisimofisi.yenimahalle.bel.tr" target="_blank" rel="noopener noreferrer">
                  https://iletisimofisi.yenimahalle.bel.tr
                </a>
              </Description>
            </TextInfo>
          </Card>
        </LeftColumn>

        <RightColumn>
          <Content>
            <Heading>İletişim Bilgileri</Heading>
            <p tw="text-base leading-relaxed">{manager.managerName}</p>
            <p tw="text-base leading-relaxed">Tel: {manager.phoneNumber}</p>
            <p tw="text-base leading-relaxed">{manager.eMailAddress}</p>
          </Content>
          <Content>
            <Heading>Faaliyet Raporları</Heading>
            {/* Yıl seçimi alt satıra almak için ayrı blok içerisinde */}
            <div tw="block mt-4">
              <Label>Faaliyet Yılı: </Label>
              <YearSelect value={selectedYear} onChange={(e) => setSelectedYear(e.target.value)}>
                {years.map((year) => (
                  <option key={year} value={year}>
                    {year}
                  </option>
                ))}
              </YearSelect>
            </div>
            {activities
              .filter((activity: ActivityReport) => activity.year.toString() === selectedYear)
              .map((activity, index) => (
                <ReportLink
                  key={index}
                  onClick={() => {
                    setSelectedActivity(activity);
                    setShowModal(true);
                  }}
                >
                  {activity.description}
                </ReportLink>
              ))}
          </Content>
        </RightColumn>
      </PageContainer>
      <CustomModal
        title={selectedActivity?.description}
        show={showModal}
        size="lg"
        onHide={() => setShowModal(false)}
        footer={<CustomButton onClick={() => setShowModal(false)} label="Kapat" />}
      >
        {selectedActivity?.attachedFileList.map((file, index) => (
          <ReportLink key={index} href={file.fileUrl} target="_blank" rel="noopener noreferrer">
            {selectedActivity?.description}
          </ReportLink>
        ))}
      </CustomModal>
    </Layout>
  );
};

export default MudurlukDetay;
